import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { followResponse } from '../user.api'; // API calls
import { QueryKey } from '@/api/common/types/query-key.enum';
import { useAppToasts } from '@/hooks/use-app-toasts';
import type { User } from '../types/user';
import type { FollowResponsePayload } from '@/api/user/types/follow-response-payload';

export const useFollowResponse = () => {
  const { raiseErrorToast } = useAppToasts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: FollowResponsePayload) => followResponse(payload),
    onMutate: async ({ userId, status }: FollowResponsePayload) => {
      await queryClient.cancelQueries({ queryKey: [QueryKey.GetLoggedInUser] });

      const previousUser = queryClient.getQueryData<User>([QueryKey.GetLoggedInUser]);

      queryClient.setQueryData<User>([QueryKey.GetLoggedInUser], user => {
        if (!user) return user;
        return {
          ...user,
          followers: user.followers.map(follower => (follower.user.id === userId ? { ...follower, status } : follower)),
        };
      });

      return { previousUser };
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [QueryKey.GetStories] });
    },
    onError: (err, payload, context) => {
      if (context?.previousUser) queryClient.setQueryData<User>([QueryKey.GetLoggedInUser], context?.previousUser);
      raiseErrorToast(err.message);
    },
  });
};
